// Admin Quality Routing — Scott's screen (2026-08-26 Scott/Jess meeting).
//
// Scott took complaint routing off Jess's plate: this page shows every
// quality-ish thread Merlin tracks, where each one went, and lets Scott
// route the ones that need a home. Route-away reasons feed the router's
// immediate-learning loop ("you should never see that again"), and when he
// doesn't know the destination the "Ask Jess to advise" preset hands the
// thread over with the question attached.
//
// Backend: app/routes/signals_reports.py (/api/admin/signals/threads-tracked).
// Service: frontend/src/services/qualityRouting.ts.

import { AxiosError } from 'axios';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  filterThreads,
  LANE_LABELS,
  laneCounts,
  qualityRoutingApi,
  relativeAge,
  statusLabel,
  type Lane,
  type ThreadTimelineEvent,
  type TrackedThread,
} from '../services/qualityRouting';
import styles from './QualityRoutingAdminPage.module.css';
import QualityRoutingDeskLine from './QualityRoutingDeskLine';
import QualityRoutingInstructions from './QualityRoutingInstructions';

const LANES: Lane[] = ['all', 'needs_routing', 'routed', 'in_cases', 'done', 'dismissed'];

const ADVISOR = 'Jess Finlaw';

function errorText(err: unknown): string {
  if (err instanceof AxiosError) {
    const detail = (err.response?.data as { detail?: unknown } | undefined)?.detail;
    if (typeof detail === 'string') return detail;
  }
  return 'Something went wrong. Please try again.';
}

interface RowDetailProps {
  thread: TrackedThread;
  onUpdated: (t: TrackedThread) => void;
}

function RowDetail({ thread, onUpdated }: RowDetailProps): React.ReactElement {
  const [timeline, setTimeline] = useState<ThreadTimelineEvent[] | null>(null);
  const [timelineError, setTimelineError] = useState(false);
  const [routeTo, setRouteTo] = useState('');
  const [note, setNote] = useState('');
  const [dismissReason, setDismissReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDismiss, setConfirmDismiss] = useState(false);

  useEffect(() => {
    let cancelled = false;
    qualityRoutingApi
      .timeline(thread.id)
      .then((events) => {
        if (!cancelled) setTimeline(events);
      })
      .catch(() => {
        if (!cancelled) setTimelineError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [thread.id]);

  const routable = thread.status !== 'in_investigation';
  const dismissible = thread.status === 'open' || thread.status === 'routed';

  const doRoute = async () => {
    if (!routeTo.trim()) {
      setError('Say who this should go to.');
      return;
    }
    // The reason is the learning signal — require it whenever this actually
    // moves the thread to a different person.
    const changesRoutee =
      routeTo.trim().toLowerCase() !== (thread.routed_to ?? '').trim().toLowerCase();
    if (changesRoutee && !note.trim()) {
      setError('Say why — the router learns from it so it stops mis-routing.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const updated = await qualityRoutingApi.route(
        thread.id,
        routeTo.trim(),
        note.trim() || undefined,
      );
      onUpdated(updated);
      setRouteTo('');
      setNote('');
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const doDismiss = async () => {
    if (!dismissReason.trim()) {
      setError('A dismissal reason is required — it teaches the router.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const updated = await qualityRoutingApi.dismiss(thread.id, dismissReason.trim());
      onUpdated(updated);
      setDismissReason('');
      setConfirmDismiss(false);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.detail}>
      <div className={styles.detailCols}>
        <div>
          {thread.summary && (
            <>
              <div className={styles.detailLabel}>Merlin&apos;s read</div>
              <p className={styles.detailText}>{thread.summary}</p>
            </>
          )}
          {thread.recommended_action && (
            <>
              <div className={styles.detailLabel}>Recommended action</div>
              <p className={styles.detailText}>{thread.recommended_action}</p>
            </>
          )}
          {thread.dismissed_reason && (
            <>
              <div className={styles.detailLabel}>Dismissed because</div>
              <p className={styles.detailText}>{thread.dismissed_reason}</p>
            </>
          )}
          {thread.work_state_note && (
            <>
              <div className={styles.detailLabel}>Where it stands</div>
              <p className={styles.detailText}>{thread.work_state_note}</p>
            </>
          )}
        </div>
        <div>
          <div className={styles.detailLabel}>Timeline</div>
          {timeline === null && !timelineError && (
            <p className={styles.detailText}>Loading…</p>
          )}
          {timelineError && (
            <p className={styles.detailText}>Timeline unavailable right now.</p>
          )}
          {timeline !== null && timeline.length === 0 && (
            <p className={styles.detailText}>No recorded events yet.</p>
          )}
          {timeline !== null && timeline.length > 0 && (
            <ul className={styles.timeline}>
              {timeline.map((e, i) => (
                <li key={i} data-kind={e.actor_kind}>
                  <span className={styles.timelineSummary}>{e.summary}</span>
                  <span className={styles.timelineWhen}>
                    {relativeAge(e.created_at)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {routable && (
        <div className={styles.actions}>
          <div className={styles.actionRow}>
            <input
              className={styles.input}
              placeholder="Send to… (name)"
              value={routeTo}
              onChange={(e) => setRouteTo(e.target.value)}
              disabled={busy}
            />
            <input
              className={`${styles.input} ${styles.inputWide}`}
              placeholder="Why? (the router learns from this)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={busy}
            />
            <button
              type="button"
              className={styles.primary}
              onClick={() => void doRoute()}
              disabled={busy}
            >
              {thread.status === 'routed' ? 'Re-route' : 'Route'}
            </button>
            <button
              type="button"
              className={styles.ghost}
              title={`Not sure where this goes? Hand it to ${ADVISOR} with your question.`}
              onClick={() => {
                setRouteTo(ADVISOR);
                if (!note.trim()) setNote('Not sure where this belongs — can you advise?');
              }}
              disabled={busy}
            >
              Ask Jess to advise
            </button>
          </div>
          {dismissible && !confirmDismiss && (
            <button
              type="button"
              className={styles.linkDanger}
              onClick={() => setConfirmDismiss(true)}
              disabled={busy}
            >
              Not an issue…
            </button>
          )}
          {dismissible && confirmDismiss && (
            <div className={styles.actionRow}>
              <input
                className={`${styles.input} ${styles.inputWide}`}
                placeholder="Why is this not an issue? (required)"
                value={dismissReason}
                onChange={(e) => setDismissReason(e.target.value)}
                disabled={busy}
              />
              <button
                type="button"
                className={styles.danger}
                onClick={() => void doDismiss()}
                disabled={busy}
              >
                Dismiss
              </button>
              <button
                type="button"
                className={styles.ghost}
                onClick={() => {
                  setConfirmDismiss(false);
                  setDismissReason('');
                }}
                disabled={busy}
              >
                Cancel
              </button>
            </div>
          )}
          {error && <div className={styles.error}>{error}</div>}
        </div>
      )}
      {!routable && (
        <p className={styles.detailText}>
          A case file is open on this thread — routing is closed.
        </p>
      )}
    </div>
  );
}

export function QualityRoutingAdminPage(): React.ReactElement {
  const [threads, setThreads] = useState<TrackedThread[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [lane, setLane] = useState<Lane>('all');
  const [query, setQuery] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await qualityRoutingApi.list('all');
      setThreads(res.items);
    } catch (err) {
      setLoadError(errorText(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const counts = useMemo(() => laneCounts(threads), [threads]);
  const visible = useMemo(
    () => filterThreads(threads, lane, query),
    [threads, lane, query],
  );

  const onUpdated = useCallback((updated: TrackedThread) => {
    setThreads((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
  }, []);

  return (
    <div className={styles.page}>
      <div className={styles.head}>
        <h1>Quality Routing</h1>
        <p>
          Every quality-ish thread Merlin tracks — who has it, why, and the ones
          still needing a home. Routing with a reason teaches the router
          immediately.
        </p>
      </div>

      {/* The rules Jess taught from the door, for curation (kanban 143454d3). */}
      <QualityRoutingInstructions />

      <div className={styles.filters}>
        <div className={styles.seg} role="tablist" aria-label="Lanes">
          {LANES.map((l) => (
            <button
              key={l}
              type="button"
              role="tab"
              aria-selected={lane === l}
              className={lane === l ? styles.on : undefined}
              onClick={() => setLane(l)}
            >
              {LANE_LABELS[l]}
              <span className={styles.count}>{counts[l]}</span>
            </button>
          ))}
        </div>
        <input
          className={styles.search}
          placeholder="Search title, QT number, person…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {loading && <p className={styles.muted}>Loading threads…</p>}
      {loadError && (
        <div className={styles.loadError}>
          {loadError}{' '}
          <button type="button" className={styles.inlineLink} onClick={() => void load()}>
            Retry
          </button>
        </div>
      )}
      {!loading && !loadError && visible.length === 0 && (
        <p className={styles.muted}>
          {threads.length === 0
            ? 'No tracked quality threads yet.'
            : 'Nothing matches this view.'}
        </p>
      )}

      {!loading && !loadError && visible.length > 0 && (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Thread</th>
                <th>Status</th>
                <th>With</th>
                <th>Why</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((t) => (
                <React.Fragment key={t.id}>
                  <tr
                    className={styles.row}
                    data-open={openId === t.id || undefined}
                    onClick={() => setOpenId(openId === t.id ? null : t.id)}
                  >
                    <td>
                      <span className={styles.threadNo}>{t.thread_no}</span>{' '}
                      <span
                        className={styles.sev}
                        data-sev={t.severity}
                        title={`Severity: ${t.severity}`}
                      />
                      <span className={styles.title}>{t.title}</span>
                      {t.source_tag === 'sales_site' && (
                        <span
                          className={styles.tag}
                          title="Came in as a confirmed sales-site complaint"
                        >
                          sales site
                        </span>
                      )}
                      {t.desk_location && (
                        <QualityRoutingDeskLine
                          thread={{ ...t, desk_location: t.desk_location }}
                        />
                      )}
                    </td>
                    <td>
                      <span className={styles.status} data-status={t.status}>
                        {statusLabel(t.status)}
                      </span>
                    </td>
                    <td>{t.routed_to ?? <span className={styles.none}>—</span>}</td>
                    <td className={styles.reason}>
                      {(t.status === 'dismissed'
                        ? t.dismissed_reason ?? t.routing_reason
                        : t.routing_reason ?? t.dismissed_reason) ?? (
                        <span className={styles.none}>—</span>
                      )}
                    </td>
                    <td className={styles.age}>{relativeAge(t.updated_at)}</td>
                  </tr>
                  {openId === t.id && (
                    <tr className={styles.detailRow}>
                      <td colSpan={5}>
                        <RowDetail thread={t} onUpdated={onUpdated} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default QualityRoutingAdminPage;
